import {useTranslation} from "react-i18next";
import React, {ReactElement} from "react";
import FormGroup from "../../../common/form/FormGroup";
import ErrorAlert from "../../../common/components/ErrorAlert";
import FormContainer from "../../../common/form/FormContainer";
import {useForm, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useFormSubmission} from "../../../common/servercall/useFormSubmission";
import * as yup from "yup";
import FormRow from "../../../common/form/FormRow";
import {CallCenterAgentSettings} from "../../../app/types";
import FormCheckbox from "../../../common/form/FormCheckbox";
import FormFieldBox from "../../../common/form/FormFieldBox";
import TextInput from "../../../common/form/TextInput";
import {regexForNumberWithoutEmergencyNr} from "../../../common/util/helpers";

const schema = yup.object().shape(
    {
        guardTimerSeconds: yup
            .number()
            .when(['useSystemDefaultGuardTimer', 'enableGuardTimer'], {
                is: (useSystemDefaultGuardTimer: boolean, enableGuardTimer: boolean) => !useSystemDefaultGuardTimer && enableGuardTimer,
                then: schema => yup.number().required('required-f').typeError('invalid-guard-timer').min(1, 'invalid-guard-timer').max(25, 'invalid-guard-timer'),
                otherwise: schema => yup.number().notRequired().nullable(),
            })
            .nullable(),
        numberConsecutiveBouncedCallsToForceAgentUnavailable: yup
            .number()
            .when(['useSystemDefaultUnavailableSettings', 'forceAgentUnavailableOnBouncedCallLimit'], {
                is: (useSystemDefaultUnavailableSettings: boolean, forceAgentUnavailableOnBouncedCallLimit: boolean) => !useSystemDefaultUnavailableSettings && forceAgentUnavailableOnBouncedCallLimit,
                then: schema => yup.number().required('required-f').typeError('invalid-bounce-limit').min(1, 'invalid-bounce-limit').max(5, 'invalid-bounce-limit'),
                otherwise: schema => yup.number().notRequired().nullable(),
            })
            .nullable(),
        wrapUpDestination: yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
            message: 'invalid-phone-number',
            excludeEmptyString: true,
        }).nullable()
    }
)

type Props = {
    callCenterAgentSettings: CallCenterAgentSettings
    onSubmit: (formData: CallCenterAgentSettings) => void
    readonly?: boolean
    buildFormButtons: (isSubmitting: boolean) => ReactElement
}

export const CallCenterAgentForm = ({callCenterAgentSettings, onSubmit, readonly, buildFormButtons}: Props) => {
    const {t} = useTranslation()

    const {control, handleSubmit, setError, setValue} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {...callCenterAgentSettings}
    })

    const {submit, isSubmitting, serverError, onError} = useFormSubmission<CallCenterAgentSettings>(onSubmit, setError)

    const useSystemDefaultGuardTimer = useWatch({
        control,
        name: 'useSystemDefaultGuardTimer',
    })

    const useSystemDefaultUnavailableSettings = useWatch({
        control,
        name: 'useSystemDefaultUnavailableSettings',
    })

    const enableGuardTimer = useWatch({
        control,
        name: 'enableGuardTimer',
    })

    const forceAgentUnavailableOnBouncedCallLimit = useWatch({
        control,
        name: 'forceAgentUnavailableOnBouncedCallLimit'
    })

    return <>
        <ErrorAlert showAlert={!!serverError}/>
        <FormContainer onSubmit={handleSubmit(submit, onError)}>
            <FormGroup label={t('settings.call-center-agent.guard-timer')} fullWidth={true}>
                <FormRow>
                    <FormFieldBox>
                        <FormCheckbox
                            label={t(`settings.call-center-agent.use-system-default`)}
                            name={`useSystemDefaultGuardTimer`}
                            control={control}
                            setValue={setValue}
                        />
                    </FormFieldBox>
                </FormRow>
                {
                    !useSystemDefaultGuardTimer &&
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox
                                    label={t(`settings.call-center-agent.guard-timer`)}
                                    name={`enableGuardTimer`}
                                    control={control}
                                    setValue={setValue}
                                />
                            </FormFieldBox>
                            {enableGuardTimer &&
                                <FormFieldBox>
                                    <TextInput readonly={readonly}
                                               label={t('settings.call-center-agent.guard-timer-seconds')}
                                               name={'guardTimerSeconds'}
                                               control={control} required={true} />
                                </FormFieldBox>
                            }
                        </FormRow>
                }

            </FormGroup>
            <FormGroup label={t('settings.call-center-agent.unavailability-settings')} fullWidth={true}>
                <FormRow>
                    <FormFieldBox>
                        <FormCheckbox
                            label={t(`settings.call-center-agent.use-system-default`)}
                            name={`useSystemDefaultUnavailableSettings`}
                            control={control}
                            setValue={setValue}
                        />
                    </FormFieldBox>
                </FormRow>
                {
                    !useSystemDefaultUnavailableSettings &&
                    [
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox
                                    label={t(`settings.call-center-agent.force-au-on-dnd-activation`)}
                                    name={`forceAgentUnavailableOnDNDActivation`}
                                    control={control}
                                    setValue={setValue}
                                />
                            </FormFieldBox>
                        </FormRow>,
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox
                                    label={t(`settings.call-center-agent.force-au-on-personal-calls`)}
                                    name={`forceAgentUnavailableOnPersonalCalls`}
                                    control={control}
                                    setValue={setValue}
                                />
                            </FormFieldBox>
                        </FormRow>,
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox
                                    label={t(`settings.call-center-agent.force-au-on-not-reachable`)}
                                    name={`forceAgentUnavailableOnNotReachable`}
                                    control={control}
                                    setValue={setValue}
                                />
                            </FormFieldBox>
                        </FormRow>,
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox
                                    label={t(`settings.call-center-agent.force-au-on-bounced-call-limit`)}
                                    name={`forceAgentUnavailableOnBouncedCallLimit`}
                                    control={control}
                                    setValue={setValue}
                                />
                            </FormFieldBox>
                            {forceAgentUnavailableOnBouncedCallLimit &&
                                <FormFieldBox>
                                    <TextInput readonly={readonly}
                                               label={t('settings.call-center-agent.bounces')}
                                               name={'numberConsecutiveBouncedCallsToForceAgentUnavailable'}
                                               control={control} required={true} />
                                </FormFieldBox>
                            }
                        </FormRow>
                    ]
                }
            </FormGroup>
            <FormGroup label={t('settings.call-center-agent.wrap-up-settings')} fullWidth={true}>
                <FormRow>
                    <FormFieldBox>
                        <TextInput readonly={readonly}
                                   label={t('settings.call-center-agent.wrap-up-destination')}
                                   name={'wrapUpDestination'}
                                   control={control} required={false} />
                    </FormFieldBox>
                </FormRow>
            </FormGroup>
            {buildFormButtons(isSubmitting)}
        </FormContainer>
    </>
}
